import { action, observable, computed } from "mobx";
import { merge } from "lodash";

import { localizationService } from "core/services";

class StepperStore {
    @observable accessor items = [];
    @observable accessor currentStep = 0;
    @observable accessor isCurrentStepValid = true;
    previousStep = 0;
    form = undefined;

    options = {
        showIconIfValid: true,
        validatePreviousStep: false,
    }

    constructor(options = null, items = null, checkGender = false) {

        if (options) merge(this.options, options);

        if (this.options.form)
            this.form = this.options.form;
        else
            throw new Error("Form is not defined.");

        this.checkGender = checkGender;

        this.addItems(items);
    }

    @action.bound
    addItems(items) {
        const localizedItems = items.map((i) => {
            if (i.hasOwnProperty("label"))
                return {
                    ...i,
                    label: localizationService.t(i.label)
                }
            else
                return {
                    ...i,
                };
        });

        this.items.push(...localizedItems);
    }

    @action.bound
    removeItems() {
        this.items = []
    }

    @action.bound
    removeItem(index) {
        this.items = this.items.filter((el, i) => i !== index);
    }

    @action.bound
    async onChange(e) {
        if (this.checkGender) {
            this.form.validate();
            this.form.showErrors();
            
            if(!this.form.$("gender").isValid)
                return;
        }

        if (this.options.onChange)
            this.options.onChange(e);
        if (this.options.validatePreviousStep)
            this.handleStepValidation(this.currentStep);

        this.currentStep = e.value;

        await this.calculateCurrentStepValidity();
    }

    @action.bound
    async increaseStep(value) {
        if ((this.currentStep + value) >= this.items.length)
            return;

        if (this.options.validatePreviousStep)
            this.handleStepValidation(this.currentStep);

        this.currentStep += value;

        await this.calculateCurrentStepValidity();
    }

    @action.bound
    async decreaseStep(value) {
        if ((this.currentStep - value) < 0)
            return;
        if (this.options.validatePreviousStep)
            this.handleStepValidation(this.currentStep);

        this.currentStep -= value;

        await this.calculateCurrentStepValidity();
    }

    @action.bound
    addItemAfter(index, newItem) {
        const itemsBefore = this.items.slice(0, index + 1);
        const itemsAfter = this.items.slice(index + 1);
        const newItems = [
            ...itemsBefore,
            newItem,
            ...itemsAfter
        ];

        this.items = newItems;
    }

    @computed
    get isFirstStep() {
        return this.currentStep === 0;
    }

    @computed
    get isLastStep() {
        return this.currentStep === this.items.length - 1;
    }

    @action.bound
    async goToStep(step) {
        if (step < 0 || step >= this.items.length)
            return;
       
        if (this.options.validatePreviousStep)
            this.handleStepValidation(step);
        
        this.currentStep = step;
        
        await this.calculateCurrentStepValidity();
    }

    @action.bound
    async goToNextStep() {
        await this.increaseStep(1);
    }

    @action.bound
    goToPreviousStep() {
        this.decreaseStep(1);
    }

    @action.bound
    handleStepValidation(step) {
        const stepFieldsForValidation = this.items[step].fieldsForValidation;
        if (!stepFieldsForValidation) return;

        let isValid = true;

        for (let i = 0; i < stepFieldsForValidation.length; i++) {
            const formField = this.form.select(stepFieldsForValidation[i]);

            if (!formField.isValid) {
                formField.validate({ showErrors: true });

                isValid = false;
            }
        }

        const steps = this.items.map((item, index) => {
            let isItemValid = item.isValid;

            if (step === index) {
                if (isValid && this.options.showIconIfValid)
                    isItemValid = true;
                else if (isValid && !this.options.showIconIfValid)
                    isItemValid = undefined;
                else
                    isItemValid = false;
            }

            return {
                ...item,
                isValid: isItemValid
            };
        });

        this.items = steps;
    }

    @action.bound
    validateAllSteps() {
        this.items.forEach((item, index) => {
            this.handleStepValidation(index);
        });
    }

    @action.bound
    async calculateCurrentStepValidity() {
        const stepFieldsForValidation = this.items[this.currentStep].fieldsForValidation;
        if (stepFieldsForValidation) {
            for (let i = 0; i < stepFieldsForValidation.length; i++) {
                const validationRes = await this.form.$(stepFieldsForValidation[i]).validate();

                if (!validationRes.isValid) {
                    this.isCurrentStepValid = false;
                    return;
                }
            }
        }

        this.isCurrentStepValid = true;

        if (this.isCurrentStepValid && !this.isLastStep) {
            this.items[this.currentStep + 1].disabled = false;
        }
    }
}

export default StepperStore;
